import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, Github, Linkedin, FileText, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMemo } from "react";

// Text animation variants for staggered letter animation
const letterVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.3 + i * 0.04,
      duration: 0.4,
    },
  }),
};

// Animated text component for letter-by-letter animation
function AnimatedText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const prefersReducedMotion = useReducedMotion();

  // Skip complex animation if reduced motion is preferred
  if (prefersReducedMotion) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {text.split("").map((char, i) => (
        <motion.span
          key={i}
          custom={i}
          variants={letterVariants}
          initial="hidden"
          animate="visible"
          className="inline-block"
          style={{ whiteSpace: char === " " ? "pre" : "normal" }}
        >
          {char}
        </motion.span>
      ))}
    </span>
  );
}

export function HeroSection() {
  const prefersReducedMotion = useReducedMotion();

  const scrollToProjects = () => {
    document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" });
  };

  // Simplified animations for reduced motion
  const fadeInVariant = useMemo(
    () => ({
      initial: prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
    }),
    [prefersReducedMotion]
  );

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-label="Hero section"
    >
      {/* Background gradient */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-background via-background to-accent/20"
        aria-hidden="true"
      />

      <div className="container-custom relative z-10">
        <header className="max-w-4xl mx-auto text-center">
          {/* Greeting */}
          <motion.p
            {...fadeInVariant}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-primary font-medium mb-4"
          >
            Hello, I'm
          </motion.p>

          {/* Name with letter animation */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight">
            <AnimatedText text="Akith Chandinu" className="text-gradient" />
          </h1>

          {/* Role with typewriter-like effect */}
          <motion.p
            {...fadeInVariant}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="text-lg sm:text-xl md:text-2xl text-muted-foreground mb-4 font-light"
          >
            {prefersReducedMotion ? (
              <span>Software Engineer | AI & Data Enthusiast</span>
            ) : (
              <motion.span
                initial={{ width: 0 }}
                animate={{ width: "auto" }}
                transition={{ duration: 0.8, delay: 1, ease: "easeOut" }}
                className="inline-block overflow-hidden whitespace-nowrap"
              >
                Software Engineer | AI & Data Enthusiast
              </motion.span>
            )}
          </motion.p>

          {/* University */}
          <motion.p
            {...fadeInVariant}
            transition={{ duration: 0.5, delay: 1.2 }}
            className="text-base sm:text-lg text-muted-foreground mb-8"
          >
            IT Undergraduate @ University of Moratuwa
          </motion.p>

          {/* CTA Buttons */}
          <motion.nav
            {...fadeInVariant}
            transition={{ duration: 0.5, delay: 1.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12"
            aria-label="Primary actions"
          >
            <Button
              size="lg"
              onClick={scrollToProjects}
              className="glow-sm hover:glow transition-all duration-300 px-8"
              aria-label="View my projects"
            >
              View Projects
              <ArrowDown className="ml-2 h-4 w-4" aria-hidden="true" />
            </Button>
            <Button variant="outline" size="lg" className="px-8" asChild>
              <a href="/cv.pdf" download aria-label="Download my CV">
                <FileText className="mr-2 h-4 w-4" aria-hidden="true" />
                Download CV
              </a>
            </Button>
          </motion.nav>

          {/* Social Links */}
          <motion.nav
            {...fadeInVariant}
            transition={{ duration: 0.5, delay: 1.6 }}
            className="flex items-center justify-center gap-4"
            aria-label="Social media links"
          >
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-primary/10 hover:text-primary transition-colors"
              asChild
            >
              <a
                href="https://github.com/Akith-002"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit my GitHub profile"
              >
                <Github className="h-5 w-5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-primary/10 hover:text-primary transition-colors"
              asChild
            >
              <a
                href="https://www.linkedin.com/in/akithchandinu"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit my LinkedIn profile"
              >
                <Linkedin className="h-5 w-5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-primary/10 hover:text-primary transition-colors"
              asChild
            >
              <a
                href="https://www.youtube.com/@akithchandinu6602"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit my YouTube channel"
              >
                <Youtube className="h-5 w-5" aria-hidden="true" />
              </a>
            </Button>
          </motion.nav>
        </header>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="text-muted-foreground"
        >
          <ArrowDown className="h-5 w-5" />
        </motion.div>
      </motion.div>
    </section>
  );
}
